import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import FAQSection from '../components/FAQSection';
import { Helmet } from "react-helmet";

const BusinessSupport = () => {
    useEffect(() => {
        document.title = "MM Auto Tech | Business Support Solutions";
        const meta = document.querySelector("meta[name='description']") || document.createElement("meta");
        meta.name = "description";
        meta.content = "MM Auto Tech offers complete B2B support to automotive businesses. Simplify operations, improve customer service, and grow revenue.";
        if (!document.head.contains(meta)) document.head.appendChild(meta);
    }, []);
    const businessSupportFAQs = [
        {
            q: "What does MM Auto Tech’s Business Support Service include?",
            a: "Our Business Support Service covers admin assistance, process automation, customer communication, scheduling, inventory control, and more—so you can focus on servicing vehicles, not chasing paperwork."
        },
        {
            q: "Is the service tailored to garage-specific workflows?",
            a: "Yes, our solutions are designed specifically for garages, MOT stations, and automotive workshops. We understand your unique operational needs and industry standards."
        },
        {
            q: "Do I need to install any software?",
            a: "No special installations are required. We help you set up cloud-based systems or integrate with tools you already use (like garage management software or CRMs)."
        },
        {
            q: "Can MM Auto Tech help if I already have a team?",
            a: "Absolutely. We complement your existing team by optimizing workflows, identifying bottlenecks, and offloading repetitive or time-consuming tasks."
        },
        {
            q: "Is support available for multi-location garages?",
            a: "Yes. Whether you're a single garage or a growing chain, our scalable support adapts to your business structure and communication setup."
        }
    ];

    return (
        <><Helmet>
            <title>Business Support Services | MM AutoTech UK</title>
            <meta
                name="description"
                content="MM AutoTech provides admin, operational, and business support to help UK garages grow efficiently and profitably." />
            <link rel="canonical" href="https://mmautotech.com/services/business-support" />
        </Helmet><div className="max-w-7xl mx-auto space-y-24">
                {/* Hero Section */}
                <section className="flex flex-col lg:flex-row items-stretch">
                    {/* Left Content */}
                    <div className="lg:w-1/2 p-8 flex flex-col justify-center">
                        <h1 className="text-sm font-semibold uppercase text-yellow-500 mb-4">Business Support</h1>
                        <h2 className="text-4xl font-bold text-blue-900 mb-6">Make Your Garage Run Smoothly with MM Auto Tech</h2>
                        <p className="text-gray-700 mb-6">Boost your efficiency, delight customers, and focus on what you do best—we’ll take care of the rest.</p>
                        <Link
                            to="/book-appointment"
                            className="inline-flex items-center justify-center text-sm font-semibold tracking-widest text-blue-900 border border-blue-900 px-6 py-2 rounded hover:bg-yellow-400 hover:border-yellow-400 transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-900/30"
                            aria-label="Book an appointment"
                        >
                            Book Free Consultation
                        </Link>
                    </div>
                    {/* Right Image */}
                    <div
                        className="lg:w-1/2 h-64 lg:h-auto relative overflow-hidden"
                        style={{ clipPath: "polygon(15% 0, 100% 0, 100% 100%, 0% 100%)" }} // clip the container
                    >
                        <img src="https://img.freepik.com/free-photo/business-people-discussion_53876-15843.jpg?semt=ais_hybrid&w=740&q=80" alt="Business Support" className="w-full h-72 md:h-full rounded shadow-lg object-cover" />
                    </div>
                </section>

                <section className="max-w-7xl mx-auto px-6 py-6 lg:py-8">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        {/* Left Content Section */}
                        <div>
                            <h3 className="text-3xl font-bold text-blue-900 mb-4">End-to-End Operational Support</h3>
                            <div className="w-16 h-1 bg-yellow-400 mb-4"></div>
                            <p className="text-gray-800 mb-4">Managing a garage means juggling schedules, inventory, staff, customers, and more. We provide expert support to help you focus on your craft—while we handle the rest.</p>
                            <p className="text-gray-800">From administrative workflows to digital systems integration, our support services are tailored for growing auto businesses like yours.</p>
                        </div>
                        {/* Right Form */}
                        <form action="https://formspree.io/f/xovlyodw" method="POST" className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block font-medium mb-1 text-sm text-blue-900">
                                        Name <span className="text-red-600">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="First"
                                        name="firstName"
                                        required
                                        className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400" />
                                </div>
                                <div>
                                    <label className="block font-medium mb-1 text-sm invisible">
                                        Last Name
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Last"
                                        name="lastName"
                                        className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block font-medium mb-1 text-sm text-blue-900">
                                        Email <span className="text-red-600">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400" />
                                </div>

                                <div>
                                    <label className="block font-medium mb-1 text-sm text-blue-900">
                                        Organization <span className="text-red-600">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="organization"
                                        required
                                        className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400" />
                                </div>
                            </div>

                            <div>
                                <label className="block font-medium mb-1 text-sm text-blue-900">
                                    Phone <span className="text-red-600">*</span>
                                </label>
                                <div className="flex items-center border border-gray-300 rounded px-2 py-2">
                                    <span className="mr-2">UK</span>
                                    <input
                                        type="tel"
                                        name="phone"
                                        placeholder="+44123456789"
                                        required
                                        className="w-full border-none focus:outline-none" />
                                </div>
                            </div>

                            <div>
                                <label className="block font-medium mb-1 text-sm text-blue-900">
                                    Dropdown <span className="text-red-600">*</span>
                                </label>
                                <select
                                    name="service"
                                    required
                                    className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
                                >
                                    <option value="">Select a service</option>
                                    <option value="Garage Management">Garage Management</option>
                                    <option value="Customer Support">Customer Support</option>
                                    <option value="Scheduling Assistance">Scheduling Assistance</option>
                                </select>
                            </div>

                            <div>
                                <button type="submit" className="px-6 py-2 border border-blue-700 text-blue-700 hover:bg-blue-700 hover:text-white rounded transition">Submit</button>
                            </div>
                        </form>
                    </div>
                </section>

                <section className="max-w-7xl mx-auto px-6 py-6 lg:py-8 md:px-16 bg-white text-center">
                    <h2 className="text-3xl font-bold mb-12 text-blue-900">Garage Management Help</h2>

                    {/* Features */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                        {/* Feature 1 */}
                        <div className="flex flex-col items-center">
                            <div className="text-blue-800 mb-4">
                                <svg
                                    className="w-10 h-10"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M9.75 3v1.5m4.5-1.5V4.5M3 9.75h1.5m15 0H21M3 14.25h1.5m15 0H21M9.75 21v-1.5m4.5 1.5V19.5M7.5 6.75h9a2.25 2.25 0 0 1 2.25 2.25v6a2.25 2.25 0 0 1-2.25 2.25h-9A2.25 2.25 0 0 1 5.25 15V9a2.25 2.25 0 0 1 2.25-2.25Z" />
                                </svg>
                            </div>
                            <h3 className="font-semibold text-lg text-gray-900">Financial Planning</h3>
                            <p className="text-sm text-blue-700 mt-2">
                                We offer advice on how to manage your money and keep your garage profitable.
                            </p>
                        </div>

                        {/* Feature 2 */}
                        <div className="flex flex-col items-center">
                            <div className="text-blue-800 mb-4">
                                <svg
                                    className="w-10 h-10"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M15 12H9m12 0A9 9 0 1 1 3 12a9 9 0 0 1 18 0Z" />
                                </svg>
                            </div>
                            <h3 className="font-semibold text-lg text-gray-900">Appointment Scheduling</h3>
                            <p className="text-sm text-blue-700 mt-2">
                                We make it easy to schedule and keep track of customer appointments.
                            </p>
                        </div>

                        {/* Feature 3 */}
                        <div className="flex flex-col items-center">
                            <div className="text-blue-800 mb-4">
                                <svg
                                    className="w-10 h-10"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M6 20.25v-1.5A2.25 2.25 0 0 1 8.25 16.5h7.5a2.25 2.25 0 0 1 2.25 2.25v1.5M12 12V3m0 9L8.25 8.25M12 12l3.75-3.75" />
                                </svg>
                            </div>
                            <h3 className="font-semibold text-lg text-gray-900">Inventory Management</h3>
                            <p className="text-sm text-blue-700 mt-2">
                                We help you keep track of all the parts and supplies you need.
                            </p>
                        </div>
                    </div>

                    {/* Car Image */}
                    <div className="flex justify-center">
                        <img
                            src="https://pngimg.com/d/bmw_PNG99543.png"
                            alt="MM Auto Jeep"
                            className="w-full max-w-md object-contain" />
                    </div>
                </section>
                {/* New Customer Acquisition */}
                <section className="text-center">
                    <h3 className="text-3xl font-bold text-blue-900 mb-12">Helping You Find New Customers</h3>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { title: "Marketing Campaigns", desc: "Targeted strategies to bring local leads to your door." },
                            { title: "Online Presence", desc: "Improve search visibility and brand awareness." },
                            { title: "CRM Systems", desc: "Track customer engagement and keep retention high." }
                        ].map((item, i) => (
                            <div key={i} className="flex flex-col items-center">
                                <div className="text-blue-800 text-3xl mb-4">&#128187;</div>
                                <h4 className="font-semibold text-lg text-gray-900">{item.title}</h4>
                                <p className="text-sm text-blue-700 mt-2">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* FAQ Section */}
                <section className="max-w-7xl mx-auto px-4">
                    <FAQSection data={businessSupportFAQs} />

                </section>

                {/* CTA: Conversion Block */}
                <section className="py-16 bg-[#0f172a] text-white px-4 sm:px-6 lg:px-8 text-center relative overflow-hidden">
                    <div className="max-w-3xl mx-auto">
                        <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                            Take the Stress Out of Garage Operations
                        </h2>
                        <p className="text-lg mb-6">
                            Let our experts handle the admin, systems, and customer flows—so you can focus on what you do best.
                        </p>
                        <Link to="/bookappointment">
                            <button className="bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-semibold px-6 py-3 rounded shadow transition-all duration-300">
                                Book Your Free Strategy Call
                            </button>
                        </Link>
                    </div>
                </section>
            </div></>
    );
};

export default BusinessSupport;
